
import { FirebaseError } from 'firebase/app';
import { toast } from '@/hooks/use-toast';

// Error codes that might occur during Firestore operations
export enum FirestoreErrorCode {
  PERMISSION_DENIED = 'permission-denied',
  NOT_FOUND = 'not-found',
  ALREADY_EXISTS = 'already-exists',
  CANCELLED = 'cancelled',
  UNKNOWN = 'unknown',
  INVALID_ARGUMENT = 'invalid-argument',
  DEADLINE_EXCEEDED = 'deadline-exceeded',
  RESOURCE_EXHAUSTED = 'resource-exhausted',
  FAILED_PRECONDITION = 'failed-precondition',
  ABORTED = 'aborted',
  OUT_OF_RANGE = 'out-of-range',
  UNIMPLEMENTED = 'unimplemented',
  INTERNAL = 'internal',
  UNAVAILABLE = 'unavailable',
  DATA_LOSS = 'data-loss',
  UNAUTHENTICATED = 'unauthenticated',
}

// Types of Firestore operations
export enum OperationType {
  READ = 'read',
  WRITE = 'write',
  DELETE = 'delete',
  UPDATE = 'update',
  INITIALIZE = 'initialize',
  SUBSCRIBE = 'subscribe',
}

// Interface for error details
export interface ErrorDetails {
  code: string;
  message: string;
  operation: OperationType;
  document?: string;
  collection?: string;
  timestamp: Date;
}

// Log Firestore error with detailed information
export const logFirestoreError = (
  error: FirebaseError | Error, 
  operation: OperationType, 
  document?: string, 
  collection?: string
): ErrorDetails => {
  const errorCode = error instanceof FirebaseError ? error.code : 'unknown';
  const errorMessage = error.message;
  
  const errorDetails: ErrorDetails = {
    code: errorCode,
    message: errorMessage,
    operation,
    document,
    collection,
    timestamp: new Date(),
  };
  
  // Log to console for debugging
  console.error(`Firestore ${operation} error:`, errorDetails);
  
  return errorDetails;
};

// Get user-friendly error message based on error code
export const getUserFriendlyErrorMessage = (error: FirebaseError | Error): string => {
  if (!(error instanceof FirebaseError)) {
    return 'An unexpected error occurred. Please try again.';
  }
  
  switch (error.code as FirestoreErrorCode) {
    case FirestoreErrorCode.PERMISSION_DENIED:
      return 'You don\'t have permission to access this data. Please check your authentication status.';
    case FirestoreErrorCode.NOT_FOUND:
      return 'The requested data was not found.';
    case FirestoreErrorCode.INVALID_ARGUMENT:
      return 'Invalid data was provided to Firestore.';
    case FirestoreErrorCode.UNAUTHENTICATED:
      return 'You need to be signed in to access this data.';
    case FirestoreErrorCode.UNAVAILABLE:
      return 'The Firestore service is currently unavailable. Please try again later.';
    default:
      return `Database error: ${error.message}`;
  }
};

// Show a toast with error information
export const showErrorToast = (error: FirebaseError | Error): void => {
  const message = getUserFriendlyErrorMessage(error);
  
  toast({
    title: 'Sync Error',
    description: message,
    variant: 'destructive',
  });
};

// Enhanced error handler that logs and shows toast
export const handleFirestoreError = (
  error: FirebaseError | Error, 
  operation: OperationType, 
  document?: string, 
  collection?: string
): ErrorDetails => {
  const errorDetails = logFirestoreError(error, operation, document, collection);
  showErrorToast(error);
  return errorDetails;
};

// Check if the user is authenticated
export const isUserAuthenticated = (): boolean => {
  const auth = getAuth();
  return !!auth.currentUser;
};

// Import this at the top after adding the function
import { getAuth } from 'firebase/auth';

